import { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import {
  enqueueGpsMutation,
  flushGpsQueue,
  newGpsId,
} from '../utils/offlineGpsQueue';
import { setGpsSnapshot } from '../utils/gpsStatusStore';
import {
  gpsWorkWindowLabel,
  shouldTrackGps,
  isWithinGpsWorkWindow,
} from '../utils/gpsWorkWindow';
import {
  GEO_FAST,
  GEO_FIRST,
  GEO_WATCH,
  assessGpsFix,
  logSkippedGps,
} from '../utils/gpsQuality';
import {
  hasGpsBgConsent,
  isNativeAndroid,
  startNativeBgWatch,
  stopNativeBgWatch,
} from '../utils/gpsNative';

/** Re-evalúa horario laboral (hora local del dispositivo). */
const SCHEDULE_TICK_MS = 60_000;

function readPosition(options, hardTimeoutMs) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ lat: null, lng: null });
      return;
    }
    let settled = false;
    const finish = (pos) => {
      if (settled) return;
      settled = true;
      resolve(pos);
    };
    const timer = setTimeout(() => finish({ lat: null, lng: null }), hardTimeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        const raw = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: Number.isFinite(Number(pos.coords.accuracy))
            ? Number(pos.coords.accuracy)
            : null,
          client_ts: new Date(pos.timestamp || Date.now()).toISOString(),
          timestamp: pos.timestamp,
        };
        const assessed = assessGpsFix(raw);
        if (!assessed.ok) {
          logSkippedGps(assessed.reason, assessed.detail);
          finish({ lat: null, lng: null, skipped: assessed.reason });
          return;
        }
        finish(assessed.pos);
      },
      () => {
        clearTimeout(timer);
        finish({ lat: null, lng: null });
      },
      options
    );
  });
}

function getPositionFast() {
  return readPosition(GEO_FAST, 9000);
}

function getPositionFirst() {
  return readPosition(GEO_FIRST, 16000);
}

/** Fresh fix obligatorio (visita / pedido): maximumAge 0. */
function getPositionFresh() {
  return readPosition(GEO_FIRST, 16000);
}

function getPosition() {
  return getPositionFresh();
}

function gpsPayload(pos, prefix) {
  return {
    lat: pos?.lat ?? null,
    lng: pos?.lng ?? null,
    accuracy: pos?.accuracy ?? null,
    client_ts: pos?.client_ts || new Date().toISOString(),
    client_point_id: newGpsId(prefix),
  };
}

function sendOrQueue(type, data, sendFn) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    enqueueGpsMutation(type, data);
    return Promise.resolve({ queued: true });
  }
  return Promise.resolve()
    .then(sendFn)
    .catch(() => {
      enqueueGpsMutation(type, data);
      return { queued: true };
    });
}

function registrarPuntoVisitaSilent(visitaId, pos) {
  if (!visitaId || pos?.lat == null || pos?.lng == null) return Promise.resolve();
  const assessed = assessGpsFix(pos);
  if (!assessed.ok) {
    logSkippedGps(assessed.reason, assessed.detail);
    return Promise.resolve({ skipped: assessed.reason });
  }
  const payload = gpsPayload(assessed.pos, 'visita');
  return sendOrQueue('punto', { visitaId, payload }, () =>
    api.registrarPuntoVisita(visitaId, payload)
  );
}

function cerrarVisitaSilent(visitaId) {
  if (!visitaId) return Promise.resolve();
  return getPositionFast().then((pos) => {
    const payload = gpsPayload(
      pos?.lat != null ? pos : { lat: null, lng: null, accuracy: null },
      'cierre'
    );
    return sendOrQueue('cerrar', { visitaId, payload }, () =>
      api.cerrarVisita(visitaId, payload)
    );
  });
}

/**
 * GPS continuo (invisible_total para el vendedor):
 * - Ventana laboral (default 08:00–20:00): ubicación viva.
 * - Fuera de ventana: solo si hay visita/pedido abierto (hasta cerrar).
 * - APK Android: BackgroundGeolocation + notificación neutra ("Oh Belleza" / "App activa").
 * - Web: watchPosition (sin fingir segundo plano).
 * - Sin diálogos, toasts ni barras de estado en la UI del vendedor.
 */
export default function useSilentGps({ vendedorId, visitaId, forceActive = false } = {}) {
  const watchRef = useRef(null);
  const nativeIdRef = useRef(null);
  const lastSendRef = useRef(0);
  const lastVisitaSendRef = useRef(0);
  const lastGoodRef = useRef(null);
  const wasActiveRef = useRef(null);
  const webPausedRef = useRef(false);
  const forceRef = useRef(Boolean(forceActive || visitaId));
  forceRef.current = Boolean(forceActive || visitaId);

  const [tracking, setTracking] = useState(() =>
    shouldTrackGps({ forceActive: Boolean(forceActive || visitaId) })
  );
  const [consentTick, setConsentTick] = useState(0);

  useEffect(() => {
    const onConsent = () => setConsentTick((n) => n + 1);
    window.addEventListener('oh:gps-consent', onConsent);
    return () => window.removeEventListener('oh:gps-consent', onConsent);
  }, []);

  useEffect(() => {
    if (!vendedorId) {
      wasActiveRef.current = null;
      return undefined;
    }

    const tick = () => {
      const active = shouldTrackGps({ forceActive: forceRef.current });
      setTracking(active);
      if (wasActiveRef.current !== null && wasActiveRef.current !== active) {
        if (typeof console !== 'undefined' && console.info) {
          const reason = forceRef.current
            ? 'visita/pedido activo'
            : `horario laboral (${gpsWorkWindowLabel()})`;
          console.info(
            active
              ? `[GPS] Tracking activo (${reason}).`
              : `[GPS] Fuera de horario (${gpsWorkWindowLabel()}) y sin visita. Tracking detenido.`
          );
        }
      }
      wasActiveRef.current = active;
    };

    tick();
    const id = window.setInterval(tick, SCHEDULE_TICK_MS);
    return () => window.clearInterval(id);
  }, [vendedorId, visitaId, forceActive]);

  useEffect(() => {
    if (!vendedorId) {
      setGpsSnapshot({ status: 'off', lat: null, lng: null, accuracy: null, updatedAt: null });
      return undefined;
    }
    const sync = () => flushGpsQueue().catch(() => {});
    sync();
    window.addEventListener('online', sync);
    const syncTimer = window.setInterval(sync, 45000);
    return () => {
      window.removeEventListener('online', sync);
      window.clearInterval(syncTimer);
    };
  }, [vendedorId]);

  useEffect(() => {
    if (!vendedorId) return undefined;

    let cancelled = false;

    const clearWebWatch = () => {
      if (watchRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = null;
      }
    };

    const clearNativeWatch = async () => {
      const id = nativeIdRef.current;
      nativeIdRef.current = null;
      if (id) await stopNativeBgWatch(id);
    };

    if (!tracking) {
      clearWebWatch();
      clearNativeWatch();
      lastGoodRef.current = null;
      setGpsSnapshot({
        status: 'off',
        lat: null,
        lng: null,
        accuracy: null,
        updatedAt: null,
      });
      return undefined;
    }

    lastSendRef.current = 0;
    lastVisitaSendRef.current = 0;
    webPausedRef.current = false;
    setGpsSnapshot({ status: 'seeking' });

    /** Sube /vendedor/ubicacion y puntos de visita ~cada 60s (no más). */
    const maybeUpload = (pos) => {
      if (webPausedRef.current) return;
      const now = Date.now();
      if (now - lastSendRef.current >= 60000 || lastSendRef.current === 0) {
        lastSendRef.current = now;
        const payload = { vendedor_id: vendedorId, ...gpsPayload(pos, 'ubicacion') };
        sendOrQueue('ubicacion', { payload }, () => api.enviarUbicacionVendedor(payload));
      }
      if (visitaId && (now - lastVisitaSendRef.current >= 60000 || lastVisitaSendRef.current === 0)) {
        lastVisitaSendRef.current = now;
        registrarPuntoVisitaSilent(visitaId, pos);
      }
    };

    const push = (rawPos) => {
      if (cancelled || webPausedRef.current) return;
      if (!shouldTrackGps({ forceActive: forceRef.current })) return;
      const assessed = assessGpsFix(rawPos);
      if (!assessed.ok) {
        logSkippedGps(assessed.reason, assessed.detail);
        if (!lastGoodRef.current) {
          setGpsSnapshot({ status: 'seeking' });
        }
        return;
      }
      const pos = assessed.pos;
      lastGoodRef.current = pos;
      setGpsSnapshot({
        status: 'ok',
        lat: pos.lat,
        lng: pos.lng,
        accuracy: pos.accuracy ?? null,
        updatedAt: Date.now(),
      });
      maybeUpload(pos);
    };

    /** Fuerza un fix fresco por si el OEM no entrega callbacks estando quieto. */
    const tickFresh = () => {
      if (cancelled) return;
      if (!shouldTrackGps({ forceActive: forceRef.current })) return;
      getPositionFresh().then((pos) => {
        if (pos?.lat != null && pos?.lng != null) push(pos);
      });
    };

    const useNative = isNativeAndroid() && hasGpsBgConsent();
    let heartbeatId = null;

    (async () => {
      if (useNative) {
        clearWebWatch();
        try {
          const id = await startNativeBgWatch({
            onLocation: push,
            onError: (err) => {
              if (cancelled) return;
              // invisible_total: sin confirm/toasts; solo estado interno para admin.
              if (err?.code === 'NOT_AUTHORIZED') {
                setGpsSnapshot({ status: 'denied' });
                return;
              }
              setGpsSnapshot({ status: 'seeking' });
            },
          });
          if (cancelled) {
            if (id) await stopNativeBgWatch(id);
            return;
          }
          nativeIdRef.current = id;
          // Primer fix fresco + latido ~60s (covers quieto / OEMs agresivos).
          tickFresh();
          heartbeatId = window.setInterval(tickFresh, 60000);
          return;
        } catch (e) {
          if (typeof console !== 'undefined' && console.warn) {
            console.warn('[GPS] Background nativo falló; usando watch web.', e);
          }
        }
      }

      // Web o fallback: sin reclamar segundo plano.
      if (!navigator.geolocation) {
        setGpsSnapshot({ status: 'unavailable' });
        return;
      }

      tickFresh();
      heartbeatId = window.setInterval(tickFresh, 60000);

      watchRef.current = navigator.geolocation.watchPosition(
        (pos) =>
          push({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: Number.isFinite(Number(pos.coords.accuracy))
              ? Number(pos.coords.accuracy)
              : null,
            client_ts: new Date(pos.timestamp || Date.now()).toISOString(),
            timestamp: pos.timestamp,
          }),
        (err) => {
          const denied = err && Number(err.code) === 1;
          setGpsSnapshot({ status: denied ? 'denied' : 'seeking' });
        },
        GEO_WATCH
      );
    })();

    return () => {
      cancelled = true;
      if (heartbeatId != null) window.clearInterval(heartbeatId);
      clearWebWatch();
      clearNativeWatch();
    };
  }, [vendedorId, visitaId, tracking, consentTick]);

  // Web: al ocultar/cerrar pestaña detener GPS y marcar soft-offline (no aplica APK).
  useEffect(() => {
    if (!vendedorId) return undefined;
    if (isNativeAndroid()) return undefined;

    let hiddenOfflineTimer = null;

    const stopWebGps = () => {
      if (watchRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = null;
      }
      lastGoodRef.current = null;
      setGpsSnapshot({
        status: 'off',
        lat: null,
        lng: null,
        accuracy: null,
        updatedAt: null,
      });
    };

    /** Cierre real de pestaña: limpia señal en vivo ya (no esperar TTL). */
    const onPageHide = () => {
      webPausedRef.current = true;
      stopWebGps();
      try {
        api.beaconUbicacionOffline();
      } catch {
        /* ignore */
      }
    };

    /** Solo pausa posts GPS; el admin cae por TTL (~5 min) si no hay pagehide. */
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        webPausedRef.current = true;
        stopWebGps();
      } else if (document.visibilityState === 'visible') {
        webPausedRef.current = false;
        if (hiddenOfflineTimer) {
          window.clearTimeout(hiddenOfflineTimer);
          hiddenOfflineTimer = null;
        }
        setConsentTick((n) => n + 1);
      }
    };

    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
      if (hiddenOfflineTimer) window.clearTimeout(hiddenOfflineTimer);
    };
  }, [vendedorId]);
}

/** Compat: solo puntos de visita (si se usa en otro sitio). */
export function useVisitaTracking(visitaId) {
  useSilentGps({ vendedorId: null, visitaId });
}

export {
  getPosition,
  getPositionFast,
  getPositionFirst,
  getPositionFresh,
  cerrarVisitaSilent,
  registrarPuntoVisitaSilent,
  isWithinGpsWorkWindow,
};
